import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../axios";
import invoiceApi from "../../api/invoiceApi";
import posSessionApi from "../../api/posSessionApi";
import { SettingsContext } from "../../contexts/SettingsContext";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";

export default function Pos() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { typeId: routeTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const queryTypeId = searchParams.get("typeId") || searchParams.get("invoiceTypeId") || searchParams.get("type");
  const returnOf = searchParams.get("returnOf");
  const initialTypeId = Number(routeTypeId || queryTypeId) || 11;
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const branchId = parseInt(localStorage.getItem("branchId"), 10);
  const userId = parseInt(localStorage.getItem("userId"), 10);
  const { decimalPlaces } = useContext(SettingsContext);

  // Security Context
  const restrictedSafeId = localStorage.getItem("safeId") ? parseInt(localStorage.getItem("safeId"), 10) : null;
  const restrictedStockId = localStorage.getItem("stockId") ? parseInt(localStorage.getItem("stockId"), 10) : null;
  const allowedSafeIds = localStorage.getItem("allowedSafeIds") ? JSON.parse(localStorage.getItem("allowedSafeIds")) : [];
  const allowedStockIds = localStorage.getItem("allowedStockIds") ? JSON.parse(localStorage.getItem("allowedStockIds")) : [];

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posSettings, setPosSettings] = useState(null); // Store POS settings
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [settings, setSettings] = useState({ isPriceIncludesVAT: false, defaultCustomerId: 0, defaultPaymentMethodId: 0, defaultSafeId: 0, defaultBankId: 0, defaultStockId: 0, autoApproveInvoices: false });
  const [creditMethodId, setCreditMethodId] = useState(null);
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);

  const [barcode, setBarcode] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnItems, setReturnItems] = useState([]);

  // Session State
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [startSessionModalOpen, setStartSessionModalOpen] = useState(false);
  const [openingBalance, setOpeningBalance] = useState("");
  
  const [closeSessionModalOpen, setCloseSessionModalOpen] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [closingBalance, setClosingBalance] = useState("");
  const [closingNotes, setClosingNotes] = useState("");
  const [parkedSlots, setParkedSlots] = useState(Array(9).fill(null)); // 9 أزرار

  useEffect(() => {
    if (!companyId) return;
    setSessionLoading(true);
    posSessionApi.getCurrentSession()
      .then(res => {
        setSession(res.data);
        setSessionLoading(false);
      })
      .catch(err => {
        setSessionLoading(false);
        if (err.response && err.response.status === 404) {
          setStartSessionModalOpen(true);
        } else {
          // toast.error("Failed to check POS session");
        }
      });
  }, [companyId]);

  async function handleStartSession() {
    const balance = parseFloat(openingBalance);
    if (isNaN(balance)) { toast.error(t('pos.messages.amountInvalid')); return; }
    try {
      const res = await posSessionApi.startSession({ openingBalance: balance });
      setSession(res.data);
      setStartSessionModalOpen(false);
      toast.success(t('pos.session.sessionStarted'));
    } catch (err) {
      toast.error("Error starting session");
    }
  }

  async function handleCloseSessionClick() {
    try {
      const res = await posSessionApi.getSessionSummary();
      setSessionSummary(res.data);
      setClosingBalance(""); // Let user enter it
      setCloseSessionModalOpen(true);
    } catch (err) {
      toast.error("Error fetching session summary");
    }
  }

  async function handleConfirmCloseSession() {
    const actual = parseFloat(closingBalance);
    if (isNaN(actual)) { toast.error(t('pos.messages.amountInvalid')); return; }
    
    try {
        const res = await posSessionApi.closeSession({ 
            closingBalance: actual,
            notes: closingNotes 
        });

        if (res.data.warning) {
          toast.warning(res.data.warning, { autoClose: 10000 });
        } else {
          toast.success(t('pos.session.sessionClosed'));
        }

        setCloseSessionModalOpen(false);
        setSession(null);
        setSessionSummary(null);
        
        // Open Report in new tab
        window.open(`/reports/pos-session/${res.data.id}`, '_blank');
        
        // Restart flow -> Show Start Modal
        setStartSessionModalOpen(true);
        setOpeningBalance("");
    } catch(err) {
        toast.error("Error closing session");
    }
  }

  const [form, setForm] = useState({
    invoiceTypeId: initialTypeId,
    invoiceNumber: "",
    issueDate: new Date().toLocaleDateString("en-CA"),
    referenceDate: new Date().toLocaleDateString("en-CA"),
    referenceNumber: "-1",
    invoiceStatus: 0,
    customerId: 0,
    supplierId: 0,
    customerPrevBalance: 0,
    stockId: 0,
    salesmanId: null,
    paymentMethodId: 0,
    safeId: 0,
    bankId: 0,
    paidAmount: 0,
    notes: "",
    discount: parseFloat(0).toFixed(decimalPlaces),
    lines: [],
  });

  const [padOpen, setPadOpen] = useState(false);
  const [padValue, setPadValue] = useState("");
  const [padTitle, setPadTitle] = useState("");
  const [padOnConfirm, setPadOnConfirm] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payMethodId, setPayMethodId] = useState(0);
  const [paySafeId, setPaySafeId] = useState(0);
  const [payBankId, setPayBankId] = useState(0);
  const [payAmount, setPayAmount] = useState("");
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  const priceIncludesVAT = !!settings.isPriceIncludesVAT;

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data || []));
    axios.get(`/Category/by-company/${companyId}`).then(r => setCategories(r.data || []));
    axios.get(`/Customer/by-company/${companyId}`).then(r => setCustomers(r.data || []));
    axios.get(`/PaymentMethod/by-company/${companyId}`).then(r => setMethods(r.data || []));
    axios.get(`/Safe/by-company/${companyId}`).then(r => setSafes(r.data || []));
    axios.get(`/Bank/by-company/${companyId}`).then(r => setBanks(r.data || []));
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data || []));
    
    // Fetch POS Settings
    axios.get('/PosSettings/by-branch', { 
        params: { 
            companyId, 
            branchId, 
            invoiceTypeId: initialTypeId // Use the current invoice type
        } 
    })
    .then(res => setPosSettings(res.data))
    .catch(err => console.error("Failed to load POS Settings", err));

  }, [companyId, initialTypeId]);

  useEffect(() => {
    const credit = methods.find(m => m.nameAr === "آجل")?.id ?? null;
    setCreditMethodId(credit);
  }, [methods]);

  useEffect(() => {
    const cashLocal = methods.find(m => m.nameAr === "نقدي")?.id;
    if (payMethodId === cashLocal) {
      let safe = form.safeId || settings.defaultSafeId || 0;
      if (restrictedSafeId) safe = restrictedSafeId;
      else if (allowedSafeIds.length > 0 && !allowedSafeIds.includes(safe)) safe = allowedSafeIds[0];
      
      setPaySafeId(safe);
      setPayBankId(0);
    } else if (payMethodId !== cashLocal && payMethodId !== creditMethodId) {
      setPayBankId(form.bankId || settings.defaultBankId || 0);
      setPaySafeId(0);
    } else {
      setPaySafeId(0);
      setPayBankId(0);
    }
  }, [payMethodId, methods, form.safeId, form.bankId, settings.defaultSafeId, settings.defaultBankId, creditMethodId, restrictedSafeId, allowedSafeIds]);

  useEffect(() => {
    if (!companyId) return;
    axios.get("/ProgramSettings/by-company-and-type", { params: { companyId, invoiceTypeId: form.invoiceTypeId } })
      .then(r => {
        const s = r.data || {};
        setSettings(s);
        const cashId = methods.find(m => m.nameAr === "نقدي")?.id;

        // Determine Final Stock
        let finalStockId = s.defaultStockId || 0;
        if (restrictedStockId) {
            finalStockId = restrictedStockId;
        } else if (allowedStockIds.length > 0) {
            if (!allowedStockIds.includes(finalStockId)) {
                finalStockId = allowedStockIds[0];
            }
        }

        // Determine Final Safe
        let finalSafeId = s.defaultSafeId || 0;
        if (restrictedSafeId) {
            finalSafeId = restrictedSafeId;
        } else if (allowedSafeIds.length > 0) {
            if (!allowedSafeIds.includes(finalSafeId)) {
                finalSafeId = allowedSafeIds[0];
            }
        }

        setForm(f => ({
          ...f,
          customerId: s.defaultCustomerId || 0,
          paymentMethodId: s.defaultPaymentMethodId || 0,
          safeId: (s.defaultPaymentMethodId || 0) === cashId ? finalSafeId : 0,
          bankId: (s.defaultPaymentMethodId || 0) !== cashId ? (s.defaultBankId || 0) : 0,
          stockId: finalStockId,
        }));
        invoiceApi.getNextNumber(companyId, branchId, form.invoiceTypeId, userId).then(res => {
          setForm(f => ({ ...f, invoiceNumber: res.data?.toString?.() || res.data }));
        });
      })
      .catch(() => {});
  }, [companyId, methods, branchId, form.invoiceTypeId, restrictedSafeId, restrictedStockId]);

  useEffect(() => {
    if (!returnOf) return;
    if (Number(form.invoiceTypeId) !== 3) return;
    if (returnModalOpen) return;
    if ((form.lines || []).length > 0) return;
    invoiceApi.getById(returnOf).then(async res => {
      const inv = res.data || {};
      const originalItems = inv.items || inv.invoiceItems || inv.details || [];
      let returnedMap = {};
      try {
        const allRes = await invoiceApi.getByCompany(companyId);
        const allInvoices = allRes.data || [];
        const relatedReturns = allInvoices.filter(x => Number(x.invoiceType) === 3 && String(x.referenceNumber || "") === String(inv.invoiceNumber || ""));
        relatedReturns.forEach(r => {
          (r.items || []).forEach(it => {
            const key = `${it.productId}-${it.unitId}`;
            const qty = parseFloat(it.quantity || 0);
            returnedMap[key] = (returnedMap[key] || 0) + qty;
          });
        });
      } catch {}
      const itemsForSelection = originalItems.map(it => {
        const pid = Number(it.productId);
        const p = products.find(pr => Number(pr.id) === pid);
        const unitsList = Array.isArray(p?.units) ? p.units : [];
        const u = unitsList.find(x => Number(x.unitId) === Number(it.unitId)) || unitsList.find(x => x.isBaseUnit) || {};
        const priceBase = parseFloat(it.unitPrice || 0);
        const taxPct = parseFloat(it.taxPercent || 0);
        const price = settings.isPriceIncludesVAT ? (priceBase * (1 + taxPct)) : priceBase;
        const key = `${it.productId}-${it.unitId}`;
        const returnedQty = parseFloat(returnedMap[key] || 0);
        const originalQty = parseFloat(it.quantity || 0);
        const availableQty = Math.max(0, originalQty - returnedQty);
        return {
          productId: pid,
          productName: p?.arabicName ?? p?.nameAr ?? it.description ?? "",
          description: it.description ?? "",
          unitId: u?.unitId || 0,
          unitName: u?.unitNameAr || "",
          originalQty,
          returnedQty,
          availableQty,
          selectQty: 0,
          price: parseFloat(price).toFixed(decimalPlaces),
          discount: parseFloat(it.discount || 0).toFixed(decimalPlaces),
          taxRate: parseFloat(taxPct).toFixed(decimalPlaces),
          unitsList,
        };
      });
      setReturnItems(itemsForSelection);
      setForm(f => ({
        ...f,
        invoiceTypeId: 3,
        referenceNumber: inv.invoiceNumber,
        referenceDate: (inv.issueDate || new Date().toISOString()).slice(0, 16),
        customerId: inv.customerId || f.customerId,
        stockId: inv.stockId || f.stockId,
      }));
      setReturnModalOpen(true);
    }).catch(() => {});
  }, [returnOf, products, decimalPlaces, settings.isPriceIncludesVAT, companyId, form.invoiceTypeId, form.lines, returnModalOpen]);

  function setReturnItemQty(i, v) {
    const val = Math.max(0, parseFloat(v || 0));
    setReturnItems(list => {
      const copy = [...list];
      const max = copy[i].availableQty;
      copy[i] = { ...copy[i], selectQty: Math.min(val, max) };
      return copy;
    });
  }

  function returnAll() {
    setReturnItems(list => list.map(it => ({ ...it, selectQty: it.availableQty })));
  }

  function applyReturnSelection() {
    const selected = returnItems.filter(it => it.selectQty > 0);
    if (selected.length === 0) { toast.error(t('pos.messages.addItemsFirst')); return; }
    const newLines = selected.map(it => ({
      productId: it.productId,
      productName: it.productName,
      description: it.description,
      unitId: it.unitId,
      unitName: it.unitName,
      qty: parseFloat(it.selectQty).toFixed(decimalPlaces),
      price: it.price,
      discount: it.discount,
      taxRate: it.taxRate,
      unitsList: it.unitsList,
    }));
    setForm(f => ({ ...f, lines: newLines }));
    setReturnModalOpen(false);
  }

  function exitReturnMode() {
    setForm(f => ({
      ...f,
      invoiceTypeId: 11,
      referenceNumber: "-1",
      referenceDate: new Date().toLocaleDateString("en-CA"),
      lines: [],
    }));
    setReturnItems([]);
    setReturnModalOpen(false);
    navigate('/pos?typeId=11', { replace: true });
  }

  useEffect(() => {
    if (Number(form.invoiceTypeId) !== 3 && returnModalOpen) {
      setReturnModalOpen(false);
    }
  }, [form.invoiceTypeId, returnModalOpen]);
  useEffect(() => {
    if (returnModalOpen && (form.lines || []).length > 0) {
      setReturnModalOpen(false);
    }
  }, [returnModalOpen, form.lines]);


  function pickUnitPriceForSale(u, p) {
    const sale = u?.saleprice ?? p?.saleprice ?? 0;
    return parseFloat(sale || 0).toFixed(decimalPlaces);
  }

  async function insertProductByBarcode(code) {
    const raw = (code || "").trim();
    if (!raw) return;
    try {
      setBarcodeLoading(true);
      let p = products.find(pr => (pr.barcode || "").toString() === raw);
      let chosenUnit = null;
      if (!p) {
        for (const pr of products) {
          const units = Array.isArray(pr.units) ? pr.units : [];
          const match = units.find(u => (u.barcode || "").toString() === raw);
          if (match) { p = pr; chosenUnit = match; break; }
        }
      }
      if (!p) { toast.error(t('pos.messages.barcodeNotFound')); return; }
      const unitsList = Array.isArray(p.units) ? p.units : [];
      const baseUnit = chosenUnit || unitsList.find(u => u.isBaseUnit) || {};
      const line = {
        productId: p.id,
        productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
        description: p?.description ?? "",
        unitId: baseUnit?.unitId || 0,
        unitName: baseUnit?.unitNameAr || "",
        qty: parseFloat(1).toFixed(decimalPlaces),
        price: pickUnitPriceForSale(baseUnit, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };
      setForm(f => ({ ...f, lines: [...f.lines, line] }));
    } finally {
      setBarcode("");
      setBarcodeLoading(false);
    }
  }

  function handleBarcodeKeyDown(e) {
    if (e.key === "Enter") { e.preventDefault(); insertProductByBarcode(barcode); }
  }

  function handleQty(i, delta) {
    setForm(f => {
      const lines = [...f.lines];
      const q = Math.max(0, parseFloat(lines[i].qty) + delta);
      lines[i] = { ...lines[i], qty: q.toFixed(decimalPlaces) };
      return { ...f, lines };
    });
  }

  function handleRemoveLine(i) {
    setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));
  }

  function handleLineChange(i, field, raw) {
    const lines = [...form.lines];
    const ln = { ...lines[i] };
    if (field === "qty") ln.qty = raw;
    if (field === "price") ln.price = raw;
    if (field === "discount") ln.discount = raw;
    if (field === "taxRate") ln.taxRate = parseFloat(raw).toFixed(decimalPlaces);
    lines[i] = ln;
    setForm(f => ({ ...f, lines }));
  }

  function setQtyForSelected(q) {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setForm(f => {
      const lines = [...f.lines];
      lines[ti] = { ...lines[ti], qty: parseFloat(q).toFixed(decimalPlaces) };
      return { ...f, lines };
    });
    setSelectedLineIndex(ti);
  }

  function incSelected(delta) {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    handleQty(ti, delta);
    setSelectedLineIndex(ti);
  }

  function removeSelectedLine() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.emptyCart')); return; }
    handleRemoveLine(ti);
    setSelectedLineIndex(null);
  }

  function clearCart() {
    setForm(f => ({ ...f, lines: [] }));
    setSelectedLineIndex(null);
  }

  function setLineDiscountPercent() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setSelectedLineIndex(ti);
    setPadTitle(t('pos.pad.titleDiscountPercent'));
    setPadValue("");
    setPadOnConfirm(() => (v) => {
      const pv = parseFloat(v);
      if (isNaN(pv)) { toast.error(t('pos.messages.percentInvalid')); return; }
      setForm(f => {
        const lines = [...f.lines];
        const ln = lines[ti];
        const qty = parseFloat(ln.qty) || 0;
        const price = parseFloat(ln.price) || 0;
        const disc = qty * price * (pv || 0) / 100;
        lines[ti] = { ...ln, discount: parseFloat(disc).toFixed(decimalPlaces) };
        return { ...f, lines };
      });
    });
    setPadOpen(true);
  }

  function setLineDiscountAmount() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setSelectedLineIndex(ti);
    setPadTitle(t('pos.pad.titleDiscountAmount'));
    setPadValue("");
    setPadOnConfirm(() => (v) => {
      const val = parseFloat(v);
      if (isNaN(val)) { toast.error(t('pos.messages.amountInvalid')); return; }
      setForm(f => {
        const lines = [...f.lines];
        lines[ti] = { ...lines[ti], discount: val.toFixed(decimalPlaces) };
        return { ...f, lines };
      });
    });
    setPadOpen(true);
  }

  function changeLinePrice() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setSelectedLineIndex(ti);
    setPadTitle(t('pos.pad.titleEnterPrice'));
    setPadValue(String(form.lines[ti]?.price || ""));
    setPadOnConfirm(() => (v) => {
      const val = parseFloat(v);
      if (isNaN(val)) { toast.error(t('pos.messages.priceInvalid')); return; }
      setForm(f => {
        const lines = [...f.lines];
        lines[ti] = { ...lines[ti], price: val.toFixed(decimalPlaces) };
        return { ...f, lines };
      });
    });
    setPadOpen(true);
  }

  function numPadBackspace() {
    setPadValue(v => v.slice(0, -1));
  }

  function numPadClear() {
    setPadValue("");
  }

  function numPadConfirm() {
    if (padOnConfirm) padOnConfirm(padValue);
    setPadOpen(false);
  }

  function numPadCancel() {
    setPadOpen(false);
  }

  function openPaidAmountPad() {
    setPadTitle(t('pos.pad.titlePaidAmount'));
    setPadValue(String(form.paidAmount || 0));
    setPadOnConfirm(() => (v) => {
      const val = parseFloat(v);
      if (isNaN(val)) { toast.error(t('pos.messages.paidAmountInvalid')); return; }
      setForm(f => ({ ...f, paidAmount: val }));
    });
    setPadOpen(true);
  }

  function openPaymentModal() {
    if ((form.lines || []).length === 0) { toast.error(t('pos.messages.emptyCart')); return; }
    
    const cashLocal = methods.find(m => m.nameAr === "نقدي")?.id;
    const defaultMethod = form.paymentMethodId || settings.defaultPaymentMethodId || 0;
    
    let initialMethod = defaultMethod;
    if (initialMethod === 0 && methods.length > 0) initialMethod = methods[0].id;

    setPayMethodId(initialMethod);

    // Determine safe/bank based on method
    if (initialMethod === cashLocal) {
        let safe = form.safeId || settings.defaultSafeId || 0;
        if (restrictedSafeId) safe = restrictedSafeId;
        else if (allowedSafeIds.length > 0 && !allowedSafeIds.includes(safe)) safe = allowedSafeIds[0];
        setPaySafeId(safe);
        setPayBankId(0);
    } else if (initialMethod !== creditMethodId) {
        setPayBankId(form.bankId || settings.defaultBankId || 0);
        setPaySafeId(0);
    } else {
        setPaySafeId(0);
        setPayBankId(0);
    }

    setPayAmount(totals.net.toFixed(decimalPlaces));
    setPayModalOpen(true);
  }

  function openCustomerModal() {
    setCustomerSearch("");
    setCustomerModalOpen(true);
  }

  function chooseCustomer(id) {
    setForm(f => ({ ...f, customerId: Number(id) }));
    setCustomerModalOpen(false);
  }

  function cancelCustomer() {
    setCustomerModalOpen(false);
  }

  function togglePriceIncludesVAT() {
    setSettings(s => ({ ...s, isPriceIncludesVAT: !s.isPriceIncludesVAT }));
  }

 function confirmPayment() {
    const amt = parseFloat(payAmount);
    if (isNaN(amt)) { toast.error(t('pos.messages.enterReceivedAmount')); return; }

    if (payMethodId === 0) { toast.error(t('pos.messages.selectPaymentMethod')); return; }

    const cashLocal = methods.find(m => m.nameAr === "نقدي")?.id;
    if (payMethodId === cashLocal && paySafeId === 0) { toast.error(t('pos.messages.selectSafe')); return; }
    if (payMethodId !== cashLocal && payMethodId !== creditMethodId && payBankId === 0) { toast.error(t('pos.messages.selectBank')); return; }

    // Prepare override data
    const paymentData = {
      paymentMethodId: payMethodId,
      safeId: payMethodId === cashLocal ? paySafeId : 0,
      bankId: (payMethodId !== cashLocal && payMethodId !== creditMethodId) ? payBankId : 0,
      paidAmount: amt,
    };

    setForm(f => ({
      ...f,
      ...paymentData
    }));

    // Pass data directly to ensure it is used
    handleSubmit(null, paymentData); 
  }


  function cancelPayment() {
    setPayModalOpen(false);
  }

  function changeLineUnit(unitId) {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setForm(f => {
      const lines = [...f.lines];
      const ln = lines[ti];
      const units = Array.isArray(ln.unitsList) ? ln.unitsList : [];
      const u = units.find(x => Number(x.unitId) === Number(unitId));
      const newPrice = u?.saleprice != null ? parseFloat(u.saleprice).toFixed(decimalPlaces) : ln.price;
      lines[ti] = { ...ln, unitId: Number(unitId), unitName: u?.unitNameAr || ln.unitName, price: newPrice };
      return { ...f, lines };
    });
    setSelectedLineIndex(ti);
  }

  function parkCurrentOrder() {
    const list = JSON.parse(localStorage.getItem("parkedOrders") || "[]");
    localStorage.setItem("parkedOrders", JSON.stringify([...list, form]));
    toast.success(t('pos.messages.orderParked'));
    setForm(f => ({ ...f, lines: [] }));
    setSelectedLineIndex(null);
  }

  function recallParkedOrder() {
    const list = JSON.parse(localStorage.getItem("parkedOrders") || "[]");
    if (!Array.isArray(list) || list.length === 0) { toast.info(t('pos.messages.noParkedOrders')); return; }
    const order = list[list.length - 1];
    const rest = list.slice(0, list.length - 1);
    localStorage.setItem("parkedOrders", JSON.stringify(rest));
    setForm(order);
    toast.success(t('pos.messages.orderRecalled'));
  }

  function calculateTotals() {
    let subtotal = 0, totalLineDiscounts = 0, totalVAT = 0;
    form.lines.forEach(ln => {
      const qty = parseFloat(ln.qty) || 0;
      const price = parseFloat(ln.price) || 0;
      const d = parseFloat(ln.discount) || 0;
      const r = parseFloat(ln.taxRate) || 0;
      const gross = qty * price;
      const afterDisc = gross - d;
      subtotal += gross;
      totalLineDiscounts += d;
      if (priceIncludesVAT) {
        const taxable = afterDisc / (1 + r);
        totalVAT += afterDisc - taxable;
      } else {
        totalVAT += afterDisc * r;
      }
    });
    const docDiscount = parseFloat(form.discount) || 0;
    const taxableAfterDocDisc = (priceIncludesVAT ? (subtotal - totalLineDiscounts) : (subtotal - totalLineDiscounts)) - docDiscount;
    const net = priceIncludesVAT ? (subtotal - totalLineDiscounts - docDiscount) : (taxableAfterDocDisc + totalVAT);
    return { subtotal, totalLineDiscounts, docDiscount, totalVAT, net, taxableAfterDocDisc };
  }

  const totals = calculateTotals();

  useEffect(() => {
    if (form.paymentMethodId !== creditMethodId) {
      setForm(f => ({ ...f, paidAmount: parseFloat(totals.net.toFixed(decimalPlaces)) }));
    }
  }, [form.paymentMethodId, totals.net, decimalPlaces, creditMethodId]);

  function handleChange(e) {
    const { name, value } = e.target;
    let newValue = value;
    if (["customerId","stockId","paymentMethodId","safeId","bankId"].includes(name)) newValue = +value;
    if (name === "discount") newValue = parseFloat(value).toFixed(decimalPlaces);
    if (name === "paidAmount") newValue = +value;
    setForm(f => ({ ...f, [name]: newValue }));
  }

  async function handleSubmit(e, overrides = {}) {
    if (e && e.preventDefault) e.preventDefault();
    
    // ✅ Merge current form with overrides (to solve async state issue)
    const data = { ...form, ...overrides };

    const fallbackCustomerId = Number(settings.defaultCustomerId || 0) || Number(customers[0]?.id || 0);
    const localCustomerId = Number(data.customerId || 0) > 0 ? Number(data.customerId) : fallbackCustomerId;
    if (!localCustomerId || localCustomerId === 0) { toast.error(t('pos.messages.customerRequired')); return; }
    if (data.paymentMethodId === 0) { toast.error(t('pos.messages.selectPaymentMethod')); return; }
    const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
    if (data.paymentMethodId === cashId && !data.safeId) { toast.error(t('pos.messages.selectSafe')); return; }
    if (data.paymentMethodId !== cashId && !data.bankId && data.paymentMethodId !== creditMethodId) { toast.error(t('pos.messages.selectBank')); return; }
    if (data.lines.length === 0) { toast.error(t('pos.messages.addItemsFirst')); return; }
    
    // Fix floating point comparison issues
    const netRounded = parseFloat(totals.net.toFixed(decimalPlaces));
    const paidRounded = parseFloat(parseFloat(data.paidAmount || 0).toFixed(decimalPlaces));
    
    if (Number(data.invoiceTypeId) !== 3 && paidRounded < netRounded) { toast.error(t('pos.messages.amountLessThanNet')); return; }

    const payloadItems = data.lines.map(ln => {
      let unitPrice = parseFloat(ln.price);
      if (priceIncludesVAT) unitPrice = unitPrice / (1 + parseFloat(ln.taxRate));
      return { productId: ln.productId, unitId: ln.unitId, quantity: parseFloat(ln.qty), unitPrice, discount: parseFloat(ln.discount), taxPercent: parseFloat(ln.taxRate), description: ln.description || "" };
    });

    const invoiceStatusToSend = settings.autoApproveInvoices ? 1 : 0;
    const payload = {
      companyId, branchId,
      invoiceTypeId: data.invoiceTypeId,
      invoiceNumber: data.invoiceNumber,
      issueDate: new Date().toISOString(),
      referenceNumber: "-1",
      referenceDate: new Date().toISOString(),
      customerId: localCustomerId,
      supplierId: 0,
      stockId: data.stockId,
      paymentMethodId: data.paymentMethodId,
      safeId: data.safeId,
      bankId: data.bankId,
      posSessionId: session?.id, // ✅ Send Session ID
      notes: "",
      discount: parseFloat(data.discount),
      invoiceStatus: invoiceStatusToSend,
      paidAmount: parseFloat(data.paidAmount || 0),
      items: payloadItems,
    };
    try {
      const res = await invoiceApi.create(payload);
      const invId = res.data?.invGlobalID;
      toast.success(invoiceStatusToSend === 0 ? t('pos.messages.invoiceSavedDraft') : t('pos.messages.invoiceSaved'));
      setReturnModalOpen(false);
      if (invId) {
        window.open(`/invoices/printPos/${invId}`, "_blank");
      }
      setForm(f => ({ ...f, lines: [], discount: parseFloat(0).toFixed(decimalPlaces) }));
      invoiceApi.getNextNumber(companyId, branchId, data.invoiceTypeId, userId).then(r => setForm(f => ({ ...f, invoiceNumber: r.data?.toString?.() || r.data })));
      setPayModalOpen(false);
      exitReturnMode();
    } catch (err) {
      const serverError = err.response?.data?.errors || err.response?.data || err.message;
      toast.error(t('pos.messages.saveErrorPrefix') + JSON.stringify(serverError));
    }
  }

  const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
  const isCash = form.paymentMethodId === cashId;
  const isCredit = form.paymentMethodId === creditMethodId;
  const isNonCash = !isCash && !isCredit;
  const netRounded = parseFloat(totals.net.toFixed(decimalPlaces));
  const changeDue = isCash ? Math.max(0, parseFloat(form.paidAmount || 0) - netRounded) : 0;

  // Theme & Logic
  const theme = useMemo(() => ({
    primary: posSettings?.primaryColor || '#2563eb',
    bg: posSettings?.backgroundColor || '#f8fafc',
    text: posSettings?.textColor || '#000000',
  }), [posSettings]);

  const visibleCategories = useMemo(() => {
    return categories.filter(c => {
      const branchMatch = c.allBranches || c.branchId === branchId;
      const showInPos = c.showInPos !== false;
      return branchMatch && showInPos;
    });
  }, [categories, branchId]);

  useEffect(() => {
    if (activeCategoryId && !visibleCategories.find(c => c.id === activeCategoryId)) {
        setActiveCategoryId(null);
    }
  }, [visibleCategories, activeCategoryId]);

  const showCats = (posSettings?.showCategories ?? true);
  const showProds = (posSettings?.showProducts ?? true);

  const filteredProducts = useMemo(() => {
    // 1. Check if products should be shown based on global POS settings
    if (!showProds) {
        return [];
    }

    let list = products;

    // Filter products based on individual ShowInPOS setting
    list = list.filter(p => p.showInPOS !== false);

    // 2. Filter products based on selected category
    if (activeCategoryId) {
        list = list.filter(p => Number(p.categoryId) === Number(activeCategoryId));
    } else {
        // Only show products from visible categories
        if (categories.length > 0) {
             const visibleCategoryIds = visibleCategories.map(c => c.id);
             list = list.filter(p => visibleCategoryIds.includes(Number(p.categoryId)));
        }
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(p => (p.arabicName || "").toLowerCase().includes(term) || (p.englishName || "").toLowerCase().includes(term));
    }
    return list;
  }, [products, activeCategoryId, searchTerm, showProds, visibleCategories, categories]);

  const displayLines = useMemo(() => (form.lines || []).map((ln, i) => ({ ln, i })).reverse(), [form.lines]);
  const modalChangeDue = Math.max(0, (parseFloat(payAmount || 0) || 0) - parseFloat(totals.net.toFixed(decimalPlaces)));
  const currentCustomerName = useMemo(() => {
    const c = customers.find(c => Number(c.id) === Number(form.customerId));
    return c?.name || t('pos.customer.cash');
  }, [customers, form.customerId, t]);
  const filteredCustomers = useMemo(() => {
    const term = customerSearch.toLowerCase().trim();
    if (!term) return customers;
    return customers.filter(c => (c.name || "").toLowerCase().includes(term));
  }, [customers, customerSearch]);

  function addProductToCart(p) {
    const unitsList = Array.isArray(p.units) ? p.units : [];
    const baseUnit = unitsList.find(u => u.isBaseUnit) || {};
    const existingIdx = form.lines.findIndex(l => l.productId === p.id && l.unitId === (baseUnit.unitId || 0));
    setForm(f => {
      const idx = f.lines.findIndex(l => l.productId === p.id && l.unitId === (baseUnit.unitId || 0));
      if (idx >= 0) {
        const lines = [...f.lines];
        const q = parseFloat(lines[idx].qty) + 1;
        lines[idx] = { ...lines[idx], qty: q.toFixed(decimalPlaces) };
        return { ...f, lines };
      }
      const line = {
        productId: p.id,
        productName: p.arabicName || p.nameAr || p.name || "",
        description: p.description || "",
        unitId: baseUnit.unitId || 0,
        unitName: baseUnit.unitNameAr || "",
        qty: parseFloat(1).toFixed(decimalPlaces),
        price: pickUnitPriceForSale(baseUnit, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };
      return { ...f, lines: [...f.lines, line] };
    });
    setSelectedLineIndex(existingIdx >= 0 ? existingIdx : form.lines.length);
  }

  let cartColSpan = "col-span-12 lg:col-span-4";
  let prodColSpan = "col-span-12 lg:col-span-6";
  let catColSpan = "col-span-12 lg:col-span-2";

  if (!showCats && !showProds) {
      cartColSpan = "col-span-12";
  } else if (!showCats) {
      prodColSpan = "col-span-12 lg:col-span-8";
  } else if (!showProds) {
      catColSpan = "col-span-12 lg:col-span-3";
      cartColSpan = "col-span-12 lg:col-span-9";
  }

  return (
    <div dir="rtl" className="min-h-screen transition-colors duration-300" style={{ backgroundColor: theme.bg, color: theme.text }}>
      {/* Top Header - Blue Bar */}
      <div className="text-white px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shadow-lg transition-colors duration-300" style={{ backgroundColor: theme.primary }}>
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
          </svg>
          <span className="font-bold text-lg">{t('pos.title')}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm">{t('pos.invoiceLabel')} {form.invoiceNumber}</span>
          <Input 
            placeholder={`🔍 ${t('pos.placeholders.search')}`} 
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)} 
            className="w-40 sm:w-48 bg-white/90 border-0 text-sm h-8 text-black"
          />
          <Input 
            placeholder={`📊 ${t('pos.placeholders.barcode')}`} 
            value={barcode} 
            onChange={e => setBarcode(e.target.value)} 
            onKeyDown={handleBarcodeKeyDown} 
            disabled={barcodeLoading} 
            className="w-32 sm:w-36 bg-white/90 border-0 text-sm h-8 text-black"
          />
          <Button 
            onClick={() => navigate('/reports/all-invoices?invoiceTypeId=11')}
            className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm"
            title={t('pos.buttons.posInvoices')}
          >
            🧾 {t('pos.buttons.posInvoices')}
          </Button>
          <Button 
            onClick={() => navigate('/reports/all-invoices?invoiceTypeId=11&invoiceStatus=1')}
            className="h-8 px-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md text-sm"
            title={t('pos.buttons.return')}
          >
            ↩️ {t('pos.buttons.return')}
          </Button>
          <Button 
            onClick={handleCloseSessionClick}
            className="h-8 px-3 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-bold"
            title={t('pos.session.closeTitle')}
          >
            🛑 {t('pos.session.close')}
          </Button>
        </div>
      </div>

      {/* Control Panel - Purple/Pink Bar */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 shadow-xl p-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            {/* Settings Dropdowns */}
          <div className="flex flex-col md:flex-row gap-2 flex-1">
            <Button 
              onClick={openCustomerModal}
              className="bg-blue-100 border-0 rounded-lg px-3 py-2 text-sm font-semibold flex-1 min-w-[160px] text-purple-700"
            >
              {`👤 ${t('invoice.fields.customer')}: ${currentCustomerName}`}
            </Button>
            <Button onClick={togglePriceIncludesVAT} title={t('programSettings.priceIncludesVAT')} className={(priceIncludesVAT ? "bg-blue-100 text-green-700 border-green-300" : "bg-amber-100 text-amber-700 border-amber-300") + " h-8 px-2 rounded-md text-xs font-bold border"}>
              {priceIncludesVAT ? t('programSettings.priceIncludesVAT') : t('pos.policy.priceExcludesVAT')}
            </Button>
          {/* Quick Number Buttons */}
          <div className="grid grid-cols-4 sm:grid-cols-9 gap-1.5 w-auto flex-shrink-0">
  {Array.from({ length: 5 }, (_, i) => (
    <Button
      key={i}
      onClick={() => {
        if (parkedSlots[i]) {
          // استرجاع الفاتورة
          setForm(parkedSlots[i]);
          setParkedSlots((slots) => {
            const copy = [...slots];
            copy[i] = null; // إزالة الفاتورة من الزر
            return copy;
          });
        } else {
          // تعليق الفاتورة الحالية
          if (!form.lines || form.lines.length === 0) {
            toast.info(t('pos.messages.emptyCart'));
            return;
          }
          setParkedSlots((slots) => {
            const copy = [...slots];
            copy[i] = { ...form }; // نسخ الفاتورة الحالية
            return copy;
          });
          setForm(f => ({ ...f, lines: [] })); // مسح السلة
          toast.success(`تم تعليق الفاتورة على الزر ${i + 1}`);
        }
      }}
      className={`h-8 sm:h-9 md:h-10 w-8 sm:w-10 md:w-11 min-w-[32px] text-xs sm:text-sm font-bold rounded ${
        parkedSlots[i]
          ? "bg-green-600 text-white hover:bg-green-700" // هناك فاتورة معلقة
          : "bg-purple-700 text-white hover:bg-purple-800" // فارغ
      }`}
    >
      {i + 1}
    </Button>
  ))}
</div>


            
          </div>

          <div className="flex gap-1.5 md:flex-nowrap flex-wrap flex-shrink-0">
            <Button onClick={removeSelectedLine} className="h-10 px-4 text-sm min-w-[88px] bg-red-600 hover:bg-red-700 text-white font-bold">
              {`🗑️ ${t('invoice.fields.delete')}`}
            </Button>
            <Button onClick={setLineDiscountPercent} className="h-10 px-4 text-sm min-w-[88px] bg-purple-700 hover:bg-purple-800 text-white font-bold">
              {`💯 ${t('pos.buttons.discountPercent')}`}
            </Button>
            <Button onClick={changeLinePrice} className="h-10 px-4 text-sm min-w-[88px] bg-orange-600 hover:bg-orange-700 text-white font-bold">
              {`✏️ ${t('pos.buttons.changePrice')}`}
            </Button>
          </div>

          {/* More Action Buttons */}
          <div className="flex flex-wrap gap-1.5">
            <Button onClick={clearCart} className="h-10 sm:h-11 md:h-12 px-4 text-sm bg-red-700 hover:bg-red-800 text-white font-bold rounded-lg">
              {`🧹 ${t('pos.buttons.clear')}`}
            </Button>
            <Button onClick={openPaymentModal} className="h-12 sm:h-12 md:h-14 px-6 text-base md:text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-extrabold rounded-xl shadow-lg">
              {`💳 ${t('pos.buttons.payNow')}`}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-3 p-3 h-[calc(100vh-180px)]">
        {/* Left Sidebar - Categories */}
        {showCats && (
        <div className={catColSpan}>
          <div className="bg-white rounded-xl shadow-lg h-full overflow-hidden">
            <div className="text-white px-3 py-2 font-bold text-sm" style={{ backgroundColor: theme.primary }}>
              {`📂 ${t('pos.headers.categories')}`}
            </div>
            <div className="p-2 overflow-y-auto h-[calc(100%-40px)]">
              <button 
                onClick={() => setActiveCategoryId(null)} 
                className={`w-full text-right px-3 py-2 rounded-lg mb-1.5 text-sm font-medium transition-all ${
                  activeCategoryId == null 
                    ? "text-white shadow-md" 
                    : "bg-gray-50 hover:bg-gray-100"
                }`}
                style={activeCategoryId == null ? { backgroundColor: theme.primary } : {}}
              >
                {t('common.all')}
              </button>
              {visibleCategories.map(c => (
                <button 
                  key={c.id} 
                  onClick={() => setActiveCategoryId(c.id)} 
                  className={`w-full text-right px-3 py-2 rounded-lg mb-1.5 text-sm font-medium transition-all ${
                    activeCategoryId === c.id 
                      ? "text-white shadow-md" 
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                  style={activeCategoryId === c.id ? { backgroundColor: theme.primary } : {}}
                >
                  {c.nameAr}
                </button>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Center - Products */}
        {showProds && (
        <div className={prodColSpan}>
          <div className="bg-white rounded-xl shadow-lg h-full overflow-hidden flex flex-col">
            <div className="text-white px-3 py-2 font-bold" style={{ backgroundColor: theme.primary }}>
              {`🛍️ ${t('pos.headers.availableProducts')}`}
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {filteredProducts.map(p => (
                  <button 
                    key={p.id} 
                    onClick={() => addProductToCart(p)} 
                    className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-4 text-right hover:shadow-xl transition-all duration-300 transform hover:scale-105 min-h-[120px]"
                    style={{ borderColor: 'transparent' }}
                  >
                    {/* Show Image if enabled */}
                    {posSettings && posSettings.showImages && p.imageUrl && (
                      <div className="w-full h-24 mb-2 overflow-hidden rounded-lg">
                        <img src={p.imageUrl} alt={p.nameAr} className="w-full h-full object-cover" />
                      </div>
                    )}

                    <div className="font-bold text-gray-900 mb-2 text-sm line-clamp-2 group-hover:text-blue-600">
                      {p.arabicName}
                    </div>
                    
                    {/* Show Barcode if enabled */}
                    {posSettings && posSettings.showBarcode && p.barcode && (
                       <div className="text-xs text-gray-500 mb-1">
                         {p.barcode}
                       </div>
                    )}

                    <div className="text-xs text-gray-500 mb-2 line-clamp-1">
                      {p.englishName}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-white px-2.5 py-1 rounded-lg font-bold text-base" style={{ backgroundColor: theme.primary }}>
                        {(p.saleprice||0).toFixed? p.saleprice.toFixed(decimalPlaces) : Number(p.saleprice||0).toFixed(decimalPlaces)}
                      </div>
                      <div className="bg-blue-100 text-blue-600 p-1.5 rounded-lg group-hover:bg-blue-500 group-hover:text-white transition-colors">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd"/>
                        </svg>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Right - Cart */}
        <div className={cartColSpan}>
          <div className="bg-white rounded-xl shadow-lg h-full overflow-hidden flex flex-col">
            <div className="text-white px-3 py-2 font-bold" style={{ backgroundColor: theme.primary }}>
              {`🛒 ${t('pos.headers.cart')}`}
            </div>
            
            {/* Totals */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-2 border-b-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                <div className="bg-white/70 rounded-lg p-2">
                  <p className="text-xs text-gray-600">{t('pos.summary.total')}</p>
                  <p className="text-base font-bold">{totals.subtotal.toFixed(decimalPlaces)}</p>
                </div>
                <div className="bg-white/70 rounded-lg p-2">
                  <p className="text-xs text-gray-600">{t('pos.summary.tax')}</p>
                  <p className="text-base font-bold text-orange-600">{totals.totalVAT.toFixed(decimalPlaces)}</p>
                </div>
                <div className="bg-white/70 rounded-lg p-2">
                  <p className="text-xs text-gray-600">{t('pos.summary.discount')}</p>
                  <p className="text-base font-bold text-purple-700">{(totals.docDiscount || 0).toFixed(decimalPlaces)}</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-2 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="font-bold">{t('pos.summary.net')}</span>
                  <span className="text-xl md:text-2xl font-extrabold">{totals.net.toFixed(decimalPlaces)}</span>
                </div>
              </div>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-100 z-10">
                  <tr>
                    <th className="px-2 py-2 text-center text-xs">#</th>
                    <th className="px-2 py-2 text-right text-xs">{t('invoice.fields.item')}</th>
                    <th className="px-2 py-2 text-center text-xs">{t('invoice.fields.quantity')}</th>
                    <th className="px-2 py-2 text-center text-xs">{t('invoice.fields.price')}</th>
                    <th className="px-2 py-2 text-center text-xs">{t('invoice.fields.net')}</th>
                    <th className="px-2 py-2 text-center text-xs">❌</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {displayLines.map(({ ln, i: origIndex }, j) => {
                    const qty = parseFloat(ln.qty);
                    const price = parseFloat(ln.price);
                    const discount = parseFloat(ln.discount);
                    const r = parseFloat(ln.taxRate);
                    const afterDisc = qty * price - discount;
                    const taxable = priceIncludesVAT ? afterDisc / (1 + r) : afterDisc;
                    const vat = priceIncludesVAT ? afterDisc - taxable : afterDisc * r;
                    const net = priceIncludesVAT ? afterDisc : afterDisc + vat;
                    
                    return (
                      <tr 
                        key={origIndex} 
                        className={`cursor-pointer transition-all ${
                          selectedLineIndex === origIndex ? "bg-indigo-50" : "hover:bg-gray-50"
                        }`}
                        onClick={() => setSelectedLineIndex(origIndex)}
                      >
                        <td className="px-2 py-2 text-center">
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full text-xs font-bold">
                            {j + 1}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-bold text-xs line-clamp-2">{ln.productName}</td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="outline" size="icon" className="h-7 w-7 text-xs bg-red-50 border-red-200 text-red-600" onClick={(e) => { e.stopPropagation(); handleQty(origIndex, -1); }}>−</Button>
                            <div className="w-10 h-7 bg-blue-50 text-blue-700 font-bold rounded border border-blue-200 flex items-center justify-center text-xs">{ln.qty}</div>
                            <Button variant="outline" size="icon" className="h-7 w-7 text-xs bg-green-50 border-green-200 text-green-600" onClick={(e) => { e.stopPropagation(); handleQty(origIndex, +1); }}>+</Button>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="inline-block bg-amber-50 text-amber-700 px-2 py-1 rounded border border-amber-200 font-bold text-xs">{ln.price}</div>
                        </td>
                        <td className="px-2 py-2 text-center font-bold text-green-600 text-sm">{net.toFixed(decimalPlaces)}</td>
                        <td className="px-2 py-2 text-center">
                          <Button variant="outline" size="icon" className="h-7 w-7 bg-red-50 hover:bg-red-500 text-red-600 hover:text-white border-red-200 rounded" onClick={(e) => { e.stopPropagation(); handleRemoveLine(origIndex); }}>🗑️</Button>
                        </td>
                      </tr>
                    );
                  })}
                  {form.lines.length === 0 && (
                    <tr><td className="px-3 py-8 text-center text-gray-400" colSpan={6}>{t('pos.messages.emptyCart')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Numpad Modal */}
      {padOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <div className="text-center font-bold text-xl mb-4">{padTitle}</div>
            <Input value={padValue} readOnly className="w-full text-center text-2xl font-bold mb-4 bg-indigo-50 border-indigo-300" />
            <div className="grid grid-cols-3 gap-2 mb-4">
              {["7","8","9","4","5","6","1","2","3","0","."].map(d => (
                <Button key={d} className="h-14 text-lg font-bold bg-gray-100-200 hover:bg-indigo-500 hover:text-white" onClick={() => setPadValue(v => v + d)}>{d}</Button>
              ))}
              <Button className="h-14 bg-amber-400 hover:bg-amber-500 text-white font-bold" onClick={numPadBackspace}>←</Button>
              <Button className="h-14 bg-orange-500 hover:bg-orange-600 text-white font-bold" onClick={numPadClear}>{t('pos.pad.clear')}</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button className="h-12 bg-green-600 hover:bg-green-700 text-white font-bold" onClick={numPadConfirm}>{`✓ ${t('pos.pad.confirm')}`}</Button>
              <Button className="h-12 bg-gray-600 hover:bg-gray-700 text-white font-bold" onClick={numPadCancel}>{`✗ ${t('pos.pad.cancel')}`}</Button>
            </div>
          </div>
        </div>
      )}
      {customerModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="text-center font-bold text-xl mb-4">{t('pos.customerModal.title')}</div>
            <Input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder={t('pos.placeholders.customerSearch')} className="w-full text-center text-lg font-bold mb-4 bg-indigo-50 border-indigo-300" />
            <div className="max-h-80 overflow-y-auto space-y-2">
              {filteredCustomers.map(c => (
                <Button key={c.id} className="w-full h-12 bg-gray-100 hover:bg-indigo-500 hover:text-white font-bold" onClick={() => chooseCustomer(c.id)}>
                  {c.name}
                </Button>
              ))}
              {filteredCustomers.length === 0 && (
                <div className="text-center text-gray-500">{t('pos.customerModal.noResults')}</div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Button className="h-12 bg-gray-600 hover:bg-gray-700 text-white font-bold" onClick={cancelCustomer}>{t('pos.customerModal.cancel')}</Button>
              <Button className="h-12 bg-green-600 hover:bg-green-700 text-white font-bold" onClick={() => setCustomerModalOpen(false)}>{t('pos.customerModal.confirm')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {payModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-4 mb-4">
              <h3 className="text-2xl font-bold mb-2">{`💳 ${t('pos.payment.title')}`}</h3>
              <div className="flex justify-between items-center bg-white/20 rounded-lg px-4 py-3">
                <span className="text-lg font-bold">{t('pos.summary.net')}</span>
                <span className="text-3xl font-extrabold">{totals.net.toFixed(decimalPlaces)}</span>
              </div>
            </div>

            {payMethodId === cashId && (
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">{t('pos.payment.changeDue')}</span>
                  <span className="text-3xl font-extrabold">{modalChangeDue.toFixed(decimalPlaces)}</span>
                </div>
              </div>
            )}

            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-3 gap-2">
                {methods.map(m => {
                    const isSelected = payMethodId === m.id;
                    let colorClass = "bg-gray-100 text-gray-800 hover:bg-gray-200";
                    if (isSelected) {
                        if (m.nameAr.includes("نقدي")) colorClass = "bg-green-600 text-white hover:bg-green-700";
                        else if (m.nameAr.includes("شبكة") || m.nameAr.includes("بطاقة")) colorClass = "bg-blue-600 text-white hover:bg-blue-700";
                        else if (m.nameAr.includes("آجل")) colorClass = "bg-yellow-600 text-white hover:bg-yellow-700";
                        else colorClass = "bg-indigo-600 text-white hover:bg-indigo-700";
                    }
                    return (
                      <Button key={m.id} className={`h-12 font-bold ${colorClass}`} onClick={()=>setPayMethodId(m.id)}>{m.nameAr}</Button>
                    );
                })}
              </div>

              {/* Safe/Bank Selection */}
              {payMethodId === cashId && (
                  <div className="mt-2">
                      <label className="block text-sm font-bold mb-1 text-gray-700">{t('invoice.fields.safe')}</label>
                      <select 
                          value={paySafeId} 
                          onChange={e => setPaySafeId(Number(e.target.value))}
                          className="w-full p-2 border rounded-lg bg-gray-50 font-bold"
                          disabled={!!restrictedSafeId}
                      >
                          <option value={0}>{t('pos.messages.selectSafe')}</option>
                          {safes
                            .filter(s => {
                                if (restrictedSafeId) return s.safeId === restrictedSafeId;
                                if (allowedSafeIds.length > 0) return allowedSafeIds.includes(s.safeId);
                                return true;
                            })
                            .map(s => (
                              <option key={s.safeId} value={s.safeId}>{s.name}</option>
                          ))}
                      </select>
                  </div>
              )}

              {payMethodId !== cashId && payMethodId !== creditMethodId && payMethodId !== 0 && (
                  <div className="mt-2">
                      <label className="block text-sm font-bold mb-1 text-gray-700">{t('invoice.fields.bank')}</label>
                      <select 
                          value={payBankId} 
                          onChange={e => setPayBankId(Number(e.target.value))}
                          className="w-full p-2 border rounded-lg bg-gray-50 font-bold"
                      >
                          <option value={0}>{t('pos.messages.selectBank')}</option>
                          {banks.map(b => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                      </select>
                  </div>
              )}
            </div>

            <div className="mb-4">
              <Input value={payAmount} readOnly className="w-full text-center text-2xl font-bold bg-indigo-50 border-indigo-300" />
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {["7","8","9","4","5","6","1","2","3","0","."].map(d => (
                <Button key={d} className="h-12 text-lg font-bold bg-gray-100-200 hover:bg-indigo-500 hover:text-white" onClick={() => setPayAmount(v => v + d)}>{d}</Button>
              ))}
              <Button className="h-12 bg-amber-400 hover:bg-amber-500 text-white font-bold" onClick={() => setPayAmount(v => v.slice(0,-1))}>←</Button>
              <Button className="h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold" onClick={() => setPayAmount("")}>{t('pos.pad.clear')}</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button className="h-14 bg-green-600 hover:bg-green-700 text-white font-bold text-lg" onClick={confirmPayment}>{`✓ ${t('pos.buttons.confirm')}`}</Button>
              <Button className="h-14 bg-gray-600 hover:bg-gray-700 text-white font-bold text-lg" onClick={cancelPayment}>{`✗ ${t('pos.buttons.cancel')}`}</Button>
            </div>
          </div>
        </div>
      )}
      {returnModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-4 mb-4">
              <h3 className="text-2xl font-bold mb-2">مرتجع من فاتورة أصلية</h3>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 text-center">الصنف</th>
                    <th className="p-2 text-center">الوحدة</th>
                    <th className="p-2 text-center">الأصل</th>
                    <th className="p-2 text-center">المُرجع</th>
                    <th className="p-2 text-center">المتاح</th>
                    <th className="p-2 text-center">كمية الإرجاع</th>
                  </tr>
                </thead>
                <tbody>
                  {returnItems.map((it, idx) => (
                    <tr key={`${it.productId}-${it.unitId}`} className="border-b">
                      <td className="p-2 text-center">{it.productName}</td>
                      <td className="p-2 text-center">{it.unitName}</td>
                      <td className="p-2 text-center">{it.originalQty}</td>
                      <td className="p-2 text-center text-amber-700">{it.returnedQty}</td>
                      <td className="p-2 text-center text-green-700 font-bold">{it.availableQty}</td>
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button className="h-8 px-2 bg-gray-200" onClick={() => setReturnItemQty(idx, Math.max(0, (it.selectQty || 0) - 1))}>-</Button>
                          <Input
                            value={it.selectQty}
                            onChange={e => setReturnItemQty(idx, e.target.value)}
                            className="w-20 text-center bg-indigo-50 border-indigo-300"
                          />
                          <Button className="h-8 px-2 bg-gray-200" onClick={() => setReturnItemQty(idx, Math.min((it.selectQty || 0) + 1, it.availableQty))}>+</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {returnItems.length === 0 && (
                    <tr><td className="p-4 text-center text-gray-500" colSpan={6}>لا توجد أصناف قابلة للإرجاع</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <Button className="h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold" onClick={returnAll}>إرجاع الكل</Button>
              <Button className="h-12 bg-green-600 hover:bg-green-700 text-white font-bold" onClick={applyReturnSelection}>إضافة للسلة</Button>
              <Button className="h-12 bg-gray-600 hover:bg-gray-700 text-white font-bold" onClick={exitReturnMode}>إلغاء المرتجع</Button>
            </div>
          </div>
        </div>
      )}

      {/* Start Session Modal */}
      {startSessionModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">{t('pos.session.startTitle')}</h2>
            <div className="mb-6">
              <label className="block text-gray-600 font-bold mb-2 text-lg">{t('pos.session.openingBalance')}</label>
              <Input 
                type="number" 
                value={openingBalance} 
                onChange={e => setOpeningBalance(e.target.value)} 
                className="text-center text-3xl font-bold h-16 bg-gray-50 border-2 border-indigo-200 focus:border-indigo-500"
                autoFocus
              />
            </div>
            <Button onClick={handleStartSession} className="w-full h-14 text-xl font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg transform transition hover:scale-105">
              {t('pos.session.start')}
            </Button>
          </div>
        </div>
      )}

      {/* Close Session Modal */}
      {closeSessionModalOpen && sessionSummary && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg">
            <div className="bg-red-600 text-white rounded-t-xl p-4 -mx-6 -mt-6 mb-6">
              <h3 className="text-2xl font-bold text-center">{t('pos.session.closeTitle')}</h3>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 font-bold">{t('pos.session.openingBalance')}</span>
                <span className="text-xl font-bold">{sessionSummary.openingBalance.toFixed(decimalPlaces)}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                 <div className="p-2 bg-green-50 rounded border border-green-100">
                    <div className="text-xs text-green-800">{t('pos.session.totalCashSales')}</div>
                    <div className="font-bold text-green-700">{sessionSummary.totalCashSales.toFixed(decimalPlaces)}</div>
                 </div>
                 <div className="p-2 bg-blue-50 rounded border border-blue-100">
                    <div className="text-xs text-blue-800">{t('pos.session.totalCardSales')}</div>
                    <div className="font-bold text-blue-700">{sessionSummary.totalCardSales.toFixed(decimalPlaces)}</div>
                 </div>
                 <div className="p-2 bg-yellow-50 rounded border border-yellow-100">
                    <div className="text-xs text-yellow-800">{t('pos.session.totalCreditSales')}</div>
                    <div className="font-bold text-yellow-700">{sessionSummary.totalCreditSales.toFixed(decimalPlaces)}</div>
                 </div>
                 <div className="p-2 bg-purple-50 rounded border border-purple-100">
                    <div className="text-xs text-purple-800">{t('pos.session.totalBankTransferSales')}</div>
                    <div className="font-bold text-purple-700">{sessionSummary.totalBankTransferSales.toFixed(decimalPlaces)}</div>
                 </div>
                 <div className="p-2 bg-red-50 rounded border border-red-100 col-span-2">
                    <div className="text-xs text-red-800">{t('pos.session.totalReturns')}</div>
                    <div className="font-bold text-red-700">{sessionSummary.totalReturns.toFixed(decimalPlaces)}</div>
                 </div>
              </div>

              <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <span className="text-indigo-800 font-bold text-lg">{t('pos.session.expectedCash')}</span>
                <span className="text-2xl font-extrabold text-indigo-900">{sessionSummary.expectedCash.toFixed(decimalPlaces)}</span>
              </div>

              <div>
                <label className="block text-gray-700 font-bold mb-1">{t('pos.session.actualCash')}</label>
                <Input 
                  type="number" 
                  value={closingBalance} 
                  onChange={e => setClosingBalance(e.target.value)} 
                  className="text-center text-2xl font-bold h-12 bg-white border-2 border-gray-300 focus:border-red-500"
                  autoFocus
                />
              </div>

              {(parseFloat(closingBalance) || 0) !== 0 && (
                  <div className={`text-center p-2 rounded font-bold ${ (parseFloat(closingBalance) - sessionSummary.expectedCash) >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50' }`}>
                      {t('pos.session.variance')}: {(parseFloat(closingBalance) - sessionSummary.expectedCash).toFixed(decimalPlaces)}
                  </div>
              )}

              <div>
                <label className="block text-gray-700 font-bold mb-1">{t('pos.session.notes')}</label>
                <Input 
                  value={closingNotes} 
                  onChange={e => setClosingNotes(e.target.value)} 
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button onClick={handleConfirmCloseSession} className="h-12 bg-red-600 hover:bg-red-700 text-white font-bold text-lg">
                {t('pos.session.close')}
              </Button>
              <Button onClick={() => setCloseSessionModalOpen(false)} className="h-12 bg-gray-500 hover:bg-gray-600 text-white font-bold text-lg">
                {t('pos.buttons.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
